import {
  normalizeWeatherServiceUrl,
  WEATHER_FEATURESERVER_CATALOG,
} from 'components/MapPage/CorporateViewTabs/EventsTab/weatherFeatureServerCatalog';
import { HAZARD_PORTAL_LAYER_CATALOG } from './hazardPortalLayerCatalog';

/** Hub folder a seeded public layer is filed under. */
export type FederalLayerFolder =
  | 'USDA'
  | 'NOAA'
  | 'FEMA'
  | 'Fire'
  | 'APHIS'
  | 'USGS and EPA'
  | 'Wildfires'
  | 'Hurricanes'
  | 'Severe Weather'
  | 'Winter Weather'
  | 'Flooding';

export type PublicSeedLayer = {
  id: string;
  name: string;
  folder: FederalLayerFolder;
  description: string;
  /** Layer URL, service root, or GeoJSON feed. */
  serviceUrl?: string | null;
  /** Living Atlas / AGOL portal item, when the layer is loaded by item id. */
  arcgisItemId?: string | null;
  /** Drives the loader branch in syncExternalLayers. */
  itemType: 'Feature Service' | 'Map Service' | 'GeoJSON' | 'Web Map';
  defaultVisible: boolean;
};

export type FederalPublicLayer = PublicSeedLayer & {
  serviceUrl: string;
};

/**
 * Public federal feeds seeded as hub layers for every organisation.
 *
 * Layer indexes are not interchangeable between services: the APHIS entries
 * below point at ids 1/5/11 because those services publish a single layer at a
 * non-zero index, and requesting `/0` returns "layer was not found".
 *
 * Forest Service footprint layers and recent USGS earthquakes are seeded
 * visible. Other federal feeds stay hidden so a CONUS map that draws flood
 * zones, drought, and quarantine boundaries at once stays readable.
 */
export const FEDERAL_PUBLIC_LAYER_CATALOG: FederalPublicLayer[] = [
  {
    id: 'usfs-office-locations',
    name: 'Forest Service offices',
    folder: 'USDA',
    description:
      'USDA Forest Service workplaces and IT sites. Owned, leased, and shared offices; contains no personally identifiable information.',
    serviceUrl:
      'https://apps.fs.usda.gov/arcx/rest/services/EDW/EDW_FSOfficeLocations_01/MapServer/0',
    itemType: 'Feature Service',
    defaultVisible: true,
  },
  {
    id: 'usfs-administrative-forests',
    name: 'National Forest System boundaries',
    folder: 'USDA',
    description:
      'Administrative boundaries of the National Forest System, the land USDA manages directly.',
    serviceUrl:
      'https://apps.fs.usda.gov/arcx/rest/services/EDW/EDW_ForestSystemBoundaries_01/MapServer/0',
    itemType: 'Feature Service',
    defaultVisible: true,
  },
  {
    id: 'usfs-fireshed-registry',
    name: 'Fireshed Registry',
    folder: 'USDA',
    description:
      'Forest Service accounting units mapping the source of community wildfire exposure.',
    serviceUrl:
      'https://apps.fs.usda.gov/arcx/rest/services/EDW/EDW_FireshedRegistry_01/MapServer/0',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'nws-watches-warnings-noaa',
    name: 'NWS watches and warnings (NOAA)',
    folder: 'Severe Weather',
    description:
      'Active National Weather Service watches, warnings, and advisories, served by NOAA rather than a republished mirror.',
    serviceUrl:
      'https://mapservices.weather.noaa.gov/eventdriven/rest/services/WWA/watch_warn_adv/MapServer/1',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'nhc-tropical-weather-noaa',
    name: 'NHC tropical weather (NOAA)',
    folder: 'Hurricanes',
    description:
      'National Hurricane Center outlooks, forecast cones, tracks, and wind fields. Seeded as the whole service because per-storm sublayers only exist while a storm is active.',
    serviceUrl:
      'https://mapservices.weather.noaa.gov/tropical/rest/services/tropical/NHC_tropical_weather/MapServer',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'fema-disaster-designated-counties',
    name: 'Disaster designated counties',
    folder: 'FEMA',
    description:
      'Counties under a current federal disaster declaration. Drives FSA, RMA, and Rural Development response.',
    serviceUrl:
      'https://gis.fema.gov/arcgis/rest/services/FEMA/DECS_ALL/FeatureServer/0',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'us-drought-monitor',
    name: 'US Drought Monitor',
    folder: 'FEMA',
    description:
      'Current drought intensity from droughtmonitor.unl.edu. USDA is one of the authors of the weekly map.',
    serviceUrl:
      'https://gis.fema.gov/arcgis/rest/services/Partner/Drought_Current/FeatureServer/0',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'fema-nfhl-flood-hazard',
    name: 'National Flood Hazard Layer',
    folder: 'FEMA',
    description:
      'FEMA regulatory flood zones. Heavy at national scale; intended for zoomed-in facility review.',
    serviceUrl: 'https://hazards.fema.gov/arcgis/rest/services/public/NFHL/MapServer',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'wfigs-perimeters-current',
    name: 'WFIGS current fire perimeters',
    folder: 'Wildfires',
    description:
      'Interagency wildland fire perimeters of record from NIFC, updated continuously.',
    serviceUrl:
      'https://services3.arcgis.com/T4QMspbfLg3qTGWY/ArcGIS/rest/services/WFIGS_Interagency_Perimeters_Current/FeatureServer/0',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'wfigs-incident-locations-current',
    name: 'WFIGS current fire incidents',
    folder: 'Wildfires',
    description:
      'Interagency wildland fire incident points from NIFC with containment and size attributes.',
    serviceUrl:
      'https://services3.arcgis.com/T4QMspbfLg3qTGWY/ArcGIS/rest/services/WFIGS_Incident_Locations_Current/FeatureServer/0',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'aphis-federal-plant-quarantine',
    name: 'Federal plant quarantine boundaries',
    folder: 'APHIS',
    description:
      'APHIS PPQ federal quarantine areas restricting movement of regulated plant material.',
    serviceUrl:
      'https://services7.arcgis.com/2C1NQ7u6M6SXoa8p/ArcGIS/rest/services/PPQ_GIS_Federal_Quarantine_Feature_Layer/FeatureServer/1',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'aphis-hpai-livestock',
    name: 'HPAI livestock detections',
    folder: 'APHIS',
    description:
      'Highly pathogenic avian influenza detections in livestock, by state and confirmation date.',
    serviceUrl:
      'https://services7.arcgis.com/2C1NQ7u6M6SXoa8p/ArcGIS/rest/services/VS_HPAI_Livestock_Events_Feature_Layer/FeatureServer/11',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'aphis-screwworm-eradication',
    name: 'New World screwworm eradication',
    folder: 'APHIS',
    description:
      'APHIS Veterinary Services map of New World screwworm eradication progress.',
    serviceUrl:
      'https://services7.arcgis.com/2C1NQ7u6M6SXoa8p/ArcGIS/rest/services/VS_NWS_Map_of_Eradication_v3_Feature_Layer/FeatureServer/5',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'aphis-bovine-tb-herds',
    name: 'Bovine tuberculosis herds',
    folder: 'APHIS',
    description: 'Publicly released bovine tuberculosis affected herd locations.',
    serviceUrl:
      'https://services7.arcgis.com/2C1NQ7u6M6SXoa8p/ArcGIS/rest/services/VS_TB_Herd_Pub/FeatureServer/1',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'aphis-brucellosis-herds',
    name: 'Brucellosis affected herds',
    folder: 'APHIS',
    description: 'Brucellosis affected herd locations from APHIS Veterinary Services.',
    serviceUrl:
      'https://services7.arcgis.com/2C1NQ7u6M6SXoa8p/ArcGIS/rest/services/Brucellosis_Herds/FeatureServer/0',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'usgs-recent-earthquakes',
    name: 'Recent earthquakes (M2.5+, 24h)',
    folder: 'USGS and EPA',
    description:
      'USGS real-time feed of magnitude 2.5 and greater earthquakes over the past day.',
    serviceUrl:
      'https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/2.5_day.geojson',
    itemType: 'GeoJSON',
    defaultVisible: true,
  },
  {
    id: 'epa-frs-facilities',
    name: 'EPA regulated facilities',
    folder: 'USGS and EPA',
    description:
      'EPA Facility Registry Service sites, for identifying regulated neighbours during a spill or plume.',
    serviceUrl:
      'https://geodata.epa.gov/arcgis/rest/services/OEI/FRS_INTERESTS/MapServer/8',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
];

/** Folder each already-seeded NOAA/NWS weather catalog entry belongs in. */
export const WEATHER_CATALOG_FOLDER_BY_CATEGORY: Record<string, FederalLayerFolder> = {
  'Watches and warnings': 'Severe Weather',
  Tropical: 'Hurricanes',
  'Precipitation and water': 'Flooding',
  Fire: 'Wildfires',
};

export function publicSeedKey(entry: PublicSeedLayer): string {
  if (entry.arcgisItemId) return `item:${entry.arcgisItemId}`;
  if (entry.serviceUrl) {
    return `url:${normalizeWeatherServiceUrl(entry.serviceUrl)}`;
  }
  return `id:${entry.id}`;
}

/**
 * The Living Atlas weather feeds and the federal catalog, as one seed list.
 *
 * The weather entries were already being created for demo organisations, so
 * they are folded in here rather than seeded separately: matching on the
 * normalised URL means an org that already has them keeps the existing rows.
 */
export function buildPublicSeedLayers(): PublicSeedLayer[] {
  const weatherLayers: PublicSeedLayer[] = WEATHER_FEATURESERVER_CATALOG.map(
    (entry) => ({
      id: entry.id,
      name: entry.name,
      // Every weather catalog entry is a NOAA/NWS product, so an unmapped
      // category is still correctly filed under NOAA.
      folder: WEATHER_CATALOG_FOLDER_BY_CATEGORY[entry.category] ?? 'NOAA',
      description: entry.description,
      serviceUrl: entry.serviceUrl,
      itemType: 'Feature Service',
      defaultVisible: false,
    })
  );

  const seenUrls = new Set(
    weatherLayers
      .map((layer) => layer.serviceUrl)
      .filter((url): url is string => Boolean(url))
      .map((url) => normalizeWeatherServiceUrl(url))
  );
  const seenItemIds = new Set(
    weatherLayers
      .map((layer) => layer.arcgisItemId)
      .filter((itemId): itemId is string => Boolean(itemId))
  );

  const federalLayers = FEDERAL_PUBLIC_LAYER_CATALOG.filter((entry) => {
    const url = normalizeWeatherServiceUrl(entry.serviceUrl);
    if (seenUrls.has(url)) return false;
    seenUrls.add(url);
    if (entry.arcgisItemId) seenItemIds.add(entry.arcgisItemId);
    return true;
  });

  const hazardLayers = HAZARD_PORTAL_LAYER_CATALOG.filter((entry) => {
    if (seenItemIds.has(entry.arcgisItemId)) return false;
    if (
      entry.serviceUrl &&
      seenUrls.has(normalizeWeatherServiceUrl(entry.serviceUrl))
    ) {
      return false;
    }
    seenItemIds.add(entry.arcgisItemId);
    if (entry.serviceUrl) {
      seenUrls.add(normalizeWeatherServiceUrl(entry.serviceUrl));
    }
    return true;
  });

  return [...weatherLayers, ...federalLayers, ...hazardLayers];
}
